import React, { useEffect, useRef } from 'react';

interface HighQualityImageProps {
  src: string;
  alt: string;
  width: string;
  height: string;
  objectFit?: 'cover' | 'contain' | 'fill';
  objectPosition?: string;
}

export const HighQualityImage: React.FC<HighQualityImageProps> = ({
  src,
  alt,
  width,
  height,
  objectFit = 'cover',
  objectPosition = 'center center'
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Disable image smoothing for maximum quality
    ctx.imageSmoothingEnabled = false;

    const img = new Image();
    img.onload = () => {
      // Set canvas size to match display size with high DPI
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;

      // Scale context for high DPI
      ctx.scale(dpr, dpr);

      // Clear canvas
      ctx.clearRect(0, 0, rect.width, rect.height);

      if (objectFit === 'cover') {
        // Calculate dimensions for cover behavior
        const canvasRatio = rect.width / rect.height;
        const imgRatio = img.width / img.height;

        let drawWidth, drawHeight, offsetX, offsetY;

        if (imgRatio > canvasRatio) {
          // Image is wider than canvas
          drawHeight = rect.height;
          drawWidth = drawHeight * imgRatio;
          offsetX = (rect.width - drawWidth) / 2;
          offsetY = 0;
        } else {
          // Image is taller than canvas
          drawWidth = rect.width;
          drawHeight = drawWidth / imgRatio;
          offsetX = 0;
          offsetY = (rect.height - drawHeight) / 2;
        }

        // Apply object position offset
        if (objectPosition.includes('top')) {
          offsetY = 0;
        }

        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
      } else {
        // For other object-fit values, draw full image
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
      }
    };

    img.src = src;
  }, [src, objectFit, objectPosition]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width,
        height,
        display: 'block',
        imageRendering: '-webkit-optimize-quality',
      } as unknown as React.CSSProperties}
      aria-label={alt}
    />
  );
};